import { CalendarClockIcon, CheckIcon, CloudSunIcon, KeyRoundIcon, LanguagesIcon, LockIcon, MessageSquareTextIcon, MinusIcon, MoonIcon, MoreHorizontalIcon, PencilIcon, PlusIcon, SendIcon, ShieldCheckIcon, ToggleLeftIcon, Trash2Icon, type LucideIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { LoadingBlock } from '@/components/LoadingBlock';
import { PageIntro } from '@/components/PageIntro';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Field } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { geocodingProviderLabel, translationProviderLabel, weatherProviderLabel } from '@/features/automation/describe';
import { JoinSettingsForm, KeywordDialog } from '@/features/keywords/KeywordDialog';
import { useAuth } from '@/lib/auth';
import { ConsentTab } from './ConsentSettingsTab';
import { SignInTab } from './SignInTab';
import { errorMessage } from '@/lib/api';
import { ACCESS_LEVELS, CAPABILITIES, LEVEL_DESCRIPTION, LEVEL_LABEL, POLICY_LABEL, cellOn } from '@/lib/roles';
import type { AccessPolicy, FeatureFlags, JoinFlowSettings, KeywordDto, KeywordInput, TenantSettingsView } from '@/lib/types';
import { cn } from '@/lib/utils';
import { useOrgKeywordMutations, useOrgKeywords, useRevokeAllSessions, useSessionsSummary, useTenantSettings, useUpdateTenantSettings } from './queries';

const FEATURES: Array<{ key: keyof FeatureFlags; title: string; hint: string; icon: typeof LanguagesIcon }> = [
  { key: 'keywords', title: 'Keyword auto-replies', hint: 'Workers text GATE, PARKING, HOURS… to a job number and get the answer the admin set.', icon: MessageSquareTextIcon },
  { key: 'selfServiceJoin', title: 'Self-service sign-up (JOIN + QR signs)', hint: 'Workers join a job list by text; every sign-up stores a TCPA consent record.', icon: PlusIcon },
  { key: 'translation', title: 'Per-contact language & translation', hint: 'Each contact has a preferred language; senders write once and everyone receives it in their language.', icon: LanguagesIcon },
  { key: 'weatherAutomation', title: 'Weather triggers', hint: 'Rain, lightning, heat index and wind thresholds tied to the job address prompt or auto-send notices.', icon: CloudSunIcon },
  { key: 'scheduledSends', title: 'Scheduled & recurring sends', hint: 'Schedule a message for later, or set daily / weekly reminders in the job\'s local time.', icon: CalendarClockIcon },
];

const SECTIONS: Array<{ key: string; label: string; hint: string; icon: LucideIcon }> = [
  { key: 'features', label: 'Features', hint: 'What every job can do', icon: ToggleLeftIcon },
  { key: 'languages', label: 'Languages', hint: 'Offered and translated', icon: LanguagesIcon },
  { key: 'keywords', label: 'Keywords', hint: 'Answered on every number', icon: MessageSquareTextIcon },
  { key: 'quiet', label: 'Quiet hours', hint: 'When texts may go out', icon: MoonIcon },
  { key: 'automation', label: 'Automation defaults', hint: 'Weather thresholds', icon: CloudSunIcon },
  { key: 'messaging', label: 'Messaging', hint: 'Footer, length, sender label', icon: SendIcon },
  { key: 'consent', label: 'Text sign-up', hint: 'Public opt-in page', icon: MessageSquareTextIcon },
  { key: 'access', label: 'Access levels', hint: 'Who can do what', icon: ShieldCheckIcon },
  { key: 'security', label: 'Security', hint: 'Sessions and devices', icon: LockIcon },
  { key: 'signin', label: 'Sign-in', hint: 'Passwords, codes, SSO', icon: KeyRoundIcon },
];

/**
 * Organization settings, one section at a time: a section list down the left (a scrolling row on phones) and the
 * chosen section's cards on the right. The section lives in the URL (`?tab=`) so any of them can be linked.
 */
export function OrgSettingsPage() {
  const q = useTenantSettings();
  const { refreshMe } = useAuth();
  const update = useUpdateTenantSettings();
  const [params, setParams] = useSearchParams();
  const tab = SECTIONS.some((x) => x.key === params.get('tab')) ? (params.get('tab') as string) : 'features';
  const setTab = (key: string) => {
    const next = new URLSearchParams(params);
    if (key === 'features') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  if (q.isPending) return <LoadingBlock rows={6} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const view = q.data;

  const save = async (patch: Parameters<typeof update.mutateAsync>[0], okMessage = 'Settings saved') => {
    try {
      await update.mutateAsync(patch);
      await refreshMe();
      toast.success(okMessage);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const busy = update.isPending;
  const current = SECTIONS.find((x) => x.key === tab) ?? SECTIONS[0]!;
  const content: Record<string, ReactNode> = {
    features: <FeaturesTab view={view} onSave={save} busy={busy} />,
    languages: <LanguagesTab view={view} onSave={save} busy={busy} />,
    keywords: <KeywordsTab view={view} onSave={save} busy={busy} />,
    quiet: <QuietHoursTab view={view} onSave={save} busy={busy} />,
    automation: <AutomationTab view={view} onSave={save} busy={busy} />,
    messaging: <MessagingTab view={view} onSave={save} busy={busy} />,
    consent: <ConsentTab view={view} onSave={save} busy={busy} />,
    access: <AccessTab view={view} onSave={save} busy={busy} />,
    security: <SecurityTab view={view} onSave={save} busy={busy} />,
    signin: <SignInTab view={view} onSave={save} busy={busy} />,
  };

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageIntro title="Organization settings" description="What every job in your organization can do, which languages you support, when texts may go out, and the keywords every job number answers. Changes apply to every job." />
      <div className="grid gap-5 lg:grid-cols-[14rem_minmax(0,1fr)] lg:items-start lg:gap-8">
        <nav aria-label="Settings sections" className="scrollbar-none -mx-4 flex gap-1 overflow-x-auto px-4 lg:sticky lg:top-[6.5rem] lg:mx-0 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:px-0">
          {SECTIONS.map((sct) => {
            const on = sct.key === tab;
            return (
              <button
                key={sct.key}
                type="button"
                onClick={() => setTab(sct.key)}
                aria-current={on ? 'page' : undefined}
                className={cn(
                  'flex shrink-0 items-center gap-2.5 border px-3 py-2 text-left text-sm whitespace-nowrap transition-colors lg:border-x-0 lg:border-t-0 lg:border-b-0 lg:border-l-2 lg:py-2.5',
                  on ? 'bg-card text-foreground border-primary font-medium lg:bg-transparent' : 'text-muted-foreground hover:text-foreground border-transparent bg-transparent hover:bg-card/60',
                )}
              >
                <sct.icon className={cn('size-4 shrink-0', on ? 'text-foreground' : 'text-muted-foreground/70')} strokeWidth={1.75} aria-hidden />
                <span className="grid">
                  <span>{sct.label}</span>
                  <span className="text-muted-foreground hidden text-xs font-normal lg:block">{sct.hint}</span>
                </span>
              </button>
            );
          })}
        </nav>
        <div className="min-w-0" role="region" aria-label={current.label}>
          {content[tab]}
        </div>
      </div>
    </div>
  );
}

type SaveFn = (patch: Parameters<ReturnType<typeof useUpdateTenantSettings>['mutateAsync']>[0], okMessage?: string) => Promise<void>;

function Section({ title, description, children, footer }: { title: ReactNode; description?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <section className="bg-card border">
      <div className="border-b px-5 py-4">
        <h2 className="text-sm font-semibold">{title}</h2>
        {description && <p className="text-muted-foreground mt-1 text-sm">{description}</p>}
      </div>
      <div className="grid gap-4 px-5 py-4">{children}</div>
      {footer && <div className="flex items-center justify-end gap-2 border-t px-5 py-3">{footer}</div>}
    </section>
  );
}

function FeaturesTab({ view, onSave, busy }: { view: TenantSettingsView; onSave: SaveFn; busy: boolean }) {
  const { features, platformFeatures } = view.settings;
  const orgFlags = view.settings.features;
  return (
    <Section title="Features" description="Turn capabilities on or off for your whole organization. Features the platform has not enabled for you are locked; contact support to unlock them.">
      {FEATURES.map((f) => {
        const platformOn = platformFeatures[f.key];
        const on = features[f.key];
        const Icon = f.icon;
        return (
          <div key={f.key} className={cn('flex items-start justify-between gap-4 border p-3', !platformOn && 'opacity-70')}>
            <div className="flex items-start gap-3">
              <Icon className="text-muted-foreground mt-0.5 size-4 shrink-0" />
              <div>
                <Label htmlFor={`f-${f.key}`} className="text-sm font-medium">
                  {f.title}
                </Label>
                <p className="text-muted-foreground text-xs">{f.hint}</p>
                {!platformOn && (
                  <p className="text-muted-foreground mt-1 flex items-center gap-1 text-xs">
                    <LockIcon className="size-3" /> Not enabled on your plan.
                  </p>
                )}
                {f.key === 'translation' && platformOn && !view.providers.translation.available && <p className="text-warning mt-1 text-xs">No translation provider is configured on this environment; texts go out in the original language.</p>}
              </div>
            </div>
            <Switch id={`f-${f.key}`} checked={on} disabled={!platformOn || busy} onCheckedChange={(v) => void onSave({ features: { [f.key]: v } }, v ? `${f.title} turned on` : `${f.title} turned off`)} />
          </div>
        );
      })}
      <p className="text-muted-foreground text-xs">
        Org switch state: {FEATURES.filter((f) => orgFlags[f.key]).length} of {FEATURES.length} on. Changes apply immediately to every job.
      </p>
    </Section>
  );
}

function LanguagesTab({ view, onSave, busy }: { view: TenantSettingsView; onSave: SaveFn; busy: boolean }) {
  const i18n = view.settings.i18n;
  const [enabled, setEnabled] = useState<string[]>(i18n.enabledLanguages);
  const [def, setDef] = useState(i18n.defaultLanguage);
  const [auto, setAuto] = useState(i18n.autoTranslate);
  const [footers, setFooters] = useState<Record<string, string>>(i18n.footers);
  useEffect(() => {
    setEnabled(i18n.enabledLanguages);
    setDef(i18n.defaultLanguage);
    setAuto(i18n.autoTranslate);
    setFooters(i18n.footers);
  }, [i18n]);
  const dirty = JSON.stringify({ enabled, def, auto, footers }) !== JSON.stringify({ enabled: i18n.enabledLanguages, def: i18n.defaultLanguage, auto: i18n.autoTranslate, footers: i18n.footers });
  const toggle = (code: string) => setEnabled((e) => (e.includes(code) ? (code === def ? e : e.filter((x) => x !== code)) : [...e, code]));
  return (
    <Section
      title="Languages"
      description="Languages offered when contacts are added, on the JOIN sign-up flow, and used to translate outbound texts. Opt-out footers keep STOP in English because carriers key on it."
      footer={
        <Button disabled={!dirty || busy} onClick={() => void onSave({ i18n: { defaultLanguage: def, enabledLanguages: enabled, autoTranslate: auto, footers } })}>
          Save languages
        </Button>
      }
    >
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {view.languages.map((l) => {
          const on = enabled.includes(l.code);
          return (
            <label key={l.code} className={cn('flex items-center justify-between gap-3 border p-2.5 text-sm', on && 'border-primary')}>
              <span>
                <span className="font-medium">{l.name}</span> <span className="text-muted-foreground text-xs">{l.nativeName}</span>
              </span>
              <span className="flex items-center gap-2">
                {on && (
                  <button type="button" onClick={() => setDef(l.code)} className={cn('text-[11px]', def === l.code ? 'text-primary font-semibold' : 'text-muted-foreground hover:underline')}>
                    {def === l.code ? 'Default' : 'Make default'}
                  </button>
                )}
                <Switch checked={on} onCheckedChange={() => toggle(l.code)} disabled={l.code === def} aria-label={`Enable ${l.name}`} />
              </span>
            </label>
          );
        })}
      </div>
      <label className="flex items-center gap-3 border p-3">
        <Switch checked={auto} onCheckedChange={setAuto} />
        <span className="grid gap-0.5">
          <span className="text-sm font-medium">Translate by default when composing</span>
          <span className="text-muted-foreground text-xs">Senders can still turn it off per message. Provider: {translationProviderLabel(view.providers.translation.name)}{view.providers.translation.available ? '' : ' (unavailable)'}.</span>
        </span>
      </label>
      <div className="grid gap-3">
        <Label>Opt-out footer per language</Label>
        {enabled
          .filter((c) => c !== def)
          .map((code) => (
            <Field key={code} label={view.languages.find((l) => l.code === code)?.name ?? code} htmlFor={`footer-${code}`} description="Leave blank to use the built-in translation.">
              <Input id={`footer-${code}`} value={footers[code] ?? ''} onChange={(e) => setFooters((f) => ({ ...f, [code]: e.target.value }))} placeholder="Responde STOP para cancelar." maxLength={160} />
            </Field>
          ))}
      </div>
    </Section>
  );
}

function KeywordsTab({ view, onSave, busy }: { view: TenantSettingsView; onSave: SaveFn; busy: boolean }) {
  const kw = view.settings.keywords;
  const list = useOrgKeywords();
  const m = useOrgKeywordMutations();
  const [dialog, setDialog] = useState<{ open: boolean; keyword: KeywordDto | null; kind: 'info' | 'join' }>({ open: false, keyword: null, kind: 'info' });
  const [deleting, setDeleting] = useState<KeywordDto | null>(null);
  const [joinDefaults, setJoinDefaults] = useState<JoinFlowSettings>(kw.joinDefaults);
  useEffect(() => setJoinDefaults(kw.joinDefaults), [kw.joinDefaults]);
  const langName = (c: string) => view.languages.find((l) => l.code === c)?.name ?? c;
  const items = list.data?.items ?? [];
  const joinDirty = JSON.stringify(joinDefaults) !== JSON.stringify(kw.joinDefaults);

  const submit = async (body: KeywordInput, id: string | null) => {
    if (id) await m.update.mutateAsync({ id, body });
    else await m.create.mutateAsync(body);
  };

  return (
    <div className="grid gap-6">
      <Section title="Policy" description="Who may add keywords.">
        <label className="flex items-start justify-between gap-4 border p-3">
          <span className="grid gap-0.5">
            <span className="text-sm font-medium">Let job admins add their own keywords</span>
            <span className="text-muted-foreground text-xs">Off = only organization admins manage keywords (here). Job admins can still see them and print signs.</span>
          </span>
          <Switch checked={kw.allowJobKeywords} disabled={busy} onCheckedChange={(v) => void onSave({ keywords: { allowJobKeywords: v } })} />
        </label>
      </Section>

      <Section
        title="Default keywords for every job"
        description="Answered on every job number unless a job overrides the same word. Use {{job.name}} in the reply to personalise it."
        footer={
          <Button onClick={() => setDialog({ open: true, keyword: null, kind: 'info' })}>
            <PlusIcon /> Add default keyword
          </Button>
        }
      >
        {items.length === 0 ? (
          <p className="text-muted-foreground text-sm">No organization-wide keywords yet. Common ones: SAFETY (your safety hotline), HR, PAY.</p>
        ) : (
          <div className="border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Keyword</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Reply</TableHead>
                  <TableHead className="text-right">Hits</TableHead>
                  <TableHead>On</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell className="font-mono font-semibold">{k.keyword}</TableCell>
                    <TableCell>
                      <Badge variant={k.kind === 'join' ? 'default' : 'outline'}>{k.kind === 'join' ? 'Sign-up' : 'Info'}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-md whitespace-normal">{k.kind === 'join' ? 'Self-service sign-up flow' : k.replyBody}</TableCell>
                    <TableCell className="text-right tabular-nums">{k.hitCount}</TableCell>
                    <TableCell>
                      <Switch checked={k.enabled} disabled={m.update.isPending} onCheckedChange={(v) => void m.update.mutateAsync({ id: k.id, body: { enabled: v } }).catch((e: unknown) => toast.error(errorMessage(e)))} />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${k.keyword}`}>
                            <MoreHorizontalIcon />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDialog({ open: true, keyword: k, kind: k.kind })}>
                            <PencilIcon /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem variant="destructive" onClick={() => setDeleting(k)}>
                            <Trash2Icon /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {!items.some((k) => k.kind === 'join') && view.settings.features.selfServiceJoin && (
          <Button variant="outline" size="sm" className="justify-self-start" onClick={() => setDialog({ open: true, keyword: null, kind: 'join' })}>
            <PlusIcon /> Turn on JOIN for every job
          </Button>
        )}
      </Section>

      <Section
        title="Sign-up flow defaults"
        description="Used by every JOIN keyword unless the keyword overrides a setting."
        footer={
          <Button disabled={!joinDirty || busy} onClick={() => void onSave({ keywords: { joinDefaults } })}>
            Save sign-up defaults
          </Button>
        }
      >
        <JoinSettingsForm settings={joinDefaults} effective={{ ...kw.joinDefaults, ...joinDefaults } as Required<JoinFlowSettings>} onChange={setJoinDefaults} jobName="Orlando Tower" languages={view.settings.i18n.enabledLanguages} langName={langName} />
      </Section>

      <KeywordDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        keyword={dialog.keyword}
        defaultKind={dialog.kind}
        languages={view.settings.i18n.enabledLanguages}
        defaultLanguage={view.settings.i18n.defaultLanguage}
        languageCatalog={view.languages}
        joinDefaults={kw.joinDefaults}
        allowJoin={view.settings.features.selfServiceJoin && !items.some((k) => k.kind === 'join')}
        jobName="your job"
        scopeLabel="Applies to every job in the organization."
        onSubmit={submit}
        busy={m.create.isPending || m.update.isPending}
      />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete ${deleting?.keyword} for every job?`}
        description="Jobs that overrode it keep their own version."
        confirmLabel="Delete"
        destructive
        loading={m.remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await m.remove.mutateAsync(deleting.id);
            toast.success('Keyword deleted');
            setDeleting(null);
          } catch (err) {
            toast.error(errorMessage(err));
          }
        }}
      />
    </div>
  );
}

function QuietHoursTab({ view, onSave, busy }: { view: TenantSettingsView; onSave: SaveFn; busy: boolean }) {
  const q = view.settings.quietHours;
  const [enabled, setEnabled] = useState(q.enabled);
  const [start, setStart] = useState(q.start);
  const [end, setEnd] = useState(q.end);
  const [tz, setTz] = useState(q.timezone);
  useEffect(() => {
    setEnabled(q.enabled);
    setStart(q.start);
    setEnd(q.end);
    setTz(q.timezone);
  }, [q]);
  const dirty = enabled !== q.enabled || start !== q.start || end !== q.end || tz !== q.timezone;
  return (
    <Section
      title={
        <span className="flex items-center gap-2">
          <MoonIcon className="size-4" /> Quiet hours
        </span>
      }
      description="TCPA safe harbor is 8:00–21:00 in the recipient's local time; some states are stricter (Florida ends at 20:00). Sends inside the window need an explicit override, scheduled and automatic sends are moved to the end of it. Jobs use their own timezone when they have an address."
      footer={
        <Button disabled={!dirty || busy} onClick={() => void onSave({ quietHours: { enabled, start, end, timezone: tz } })}>
          Save quiet hours
        </Button>
      }
    >
      <label className="flex items-center gap-3">
        <Switch checked={enabled} onCheckedChange={setEnabled} /> <span className="text-sm">{enabled ? 'Enforced' : 'Not enforced (warnings only)'}</span>
      </label>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Quiet from" htmlFor="qh-start">
          <Input id="qh-start" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
        </Field>
        <Field label="Until" htmlFor="qh-end">
          <Input id="qh-end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
        </Field>
        <Field label="Fallback timezone" htmlFor="qh-tz" description="For jobs without an address.">
          <Input id="qh-tz" value={tz} onChange={(e) => setTz(e.target.value)} placeholder="America/New_York" />
        </Field>
      </div>
    </Section>
  );
}

function AutomationTab({ view, onSave, busy }: { view: TenantSettingsView; onSave: SaveFn; busy: boolean }) {
  const a = view.settings.automation;
  const [v, setV] = useState(a);
  useEffect(() => setV(a), [a]);
  const dirty = JSON.stringify(v) !== JSON.stringify(a);
  const num = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => setV((s) => ({ ...s, [k]: Number(e.target.value) }));
  return (
    <Section
      title="Automation defaults"
      description="Pre-filled thresholds when a job admin creates a weather rule, and whether new rules draft or auto-send. Heat guidance: OSHA/NIOSH flag a heat index of 91°F as moderate risk and 103°F as high."
      footer={
        <Button disabled={!dirty || busy} onClick={() => void onSave({ automation: v })}>
          Save defaults
        </Button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Heat index (°F)" htmlFor="au-heat">
          <Input id="au-heat" type="number" value={v.heatIndexF} onChange={num('heatIndexF')} />
        </Field>
        <Field label="Chance of rain (%)" htmlFor="au-rain">
          <Input id="au-rain" type="number" value={v.rainProbability} onChange={num('rainProbability')} />
        </Field>
        <Field label="Wind / gusts (mph)" htmlFor="au-wind">
          <Input id="au-wind" type="number" value={v.windMph} onChange={num('windMph')} />
        </Field>
        <Field label="Look ahead (hours)" htmlFor="au-look">
          <Input id="au-look" type="number" min={1} max={72} value={v.lookaheadHours} onChange={num('lookaheadHours')} />
        </Field>
      </div>
      <div className="grid gap-2">
        <Label>New rules default to</Label>
        <div className="flex gap-2">
          {(['draft', 'auto'] as const).map((mode) => (
            <button key={mode} type="button" onClick={() => setV((s) => ({ ...s, defaultMode: mode }))} className={cn('border px-3 py-2 text-sm', v.defaultMode === mode ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent')}>
              {mode === 'draft' ? 'Create a draft for approval' : 'Send automatically'}
            </button>
          ))}
        </div>
      </div>
      <p className="text-muted-foreground flex items-center gap-1 text-xs">
        <ToggleLeftIcon className="size-3" /> Providers on this environment: weather {weatherProviderLabel(view.providers.weather)}, geocoding {geocodingProviderLabel(view.providers.geocoding)}.
      </p>
    </Section>
  );
}

function MessagingTab({ view, onSave, busy }: { view: TenantSettingsView; onSave: SaveFn; busy: boolean }) {
  const s = view.settings;
  const [footer, setFooter] = useState(s.optOutFooter);
  const [country, setCountry] = useState(s.defaultCountry);
  const [label, setLabel] = useState(s.senderLabelEnabled);
  const [max, setMax] = useState(s.maxMessageLength);
  useEffect(() => {
    setFooter(s.optOutFooter);
    setCountry(s.defaultCountry);
    setLabel(s.senderLabelEnabled);
    setMax(s.maxMessageLength);
  }, [s]);
  const dirty = footer !== s.optOutFooter || country !== s.defaultCountry || label !== s.senderLabelEnabled || max !== s.maxMessageLength;
  return (
    <Section
      title="Messaging defaults"
      description="Applied to every outbound text."
      footer={
        <Button disabled={!dirty || busy} onClick={() => void onSave({ optOutFooter: footer, defaultCountry: country.toUpperCase(), senderLabelEnabled: label, maxMessageLength: max })}>
          Save messaging defaults
        </Button>
      }
    >
      <Field label="Opt-out footer" htmlFor="m-footer" description="Appended to every message. Carriers require an opt-out instruction.">
        <Input id="m-footer" value={footer} onChange={(e) => setFooter(e.target.value)} maxLength={160} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Default country" htmlFor="m-country" description="For parsing phone numbers">
          <Input id="m-country" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} maxLength={2} className="uppercase" />
        </Field>
        <Field label="Max message length" htmlFor="m-max" description="Including the footer">
          <Input id="m-max" type="number" min={160} max={1600} value={max} onChange={(e) => setMax(Number(e.target.value))} />
        </Field>
        <Field label="Sender label" description='Prefix texts with the job label, e.g. "Tower B: …"'>
          <div className="flex h-9 items-center gap-2">
            <Switch checked={label} onCheckedChange={setLabel} /> <span className="text-sm">{label ? 'Enabled' : 'Disabled'}</span>
          </div>
        </Field>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------- security

const IDLE_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Never (stay signed in while the session lasts)' },
  { value: 20, label: 'After 20 minutes' },
  { value: 30, label: 'After 30 minutes' },
  { value: 60, label: 'After 1 hour' },
  { value: 120, label: 'After 2 hours' },
  { value: 240, label: 'After 4 hours' },
  { value: 480, label: 'After 8 hours' },
  { value: 720, label: 'After 12 hours' },
  { value: 1440, label: 'After 24 hours' },
];
const SESSION_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 1, label: '1 day' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
];

const selectClass = 'border-input bg-background h-9 w-full border px-3 text-sm';

const POLICY_KEYS = Object.keys(POLICY_LABEL) as Array<keyof AccessPolicy>;

/** The three access levels, what each can do, and the switches that narrow the two Project Manager levels. */
function AccessTab({ view, onSave, busy }: { view: TenantSettingsView; onSave: SaveFn; busy: boolean }) {
  const policy = view.settings.access;
  const [draft, setDraft] = useState<AccessPolicy>(policy);
  useEffect(() => setDraft(policy), [policy]);
  const dirty = POLICY_KEYS.some((k) => draft[k] !== policy[k]);

  return (
    <div className="grid gap-6">
      <Section title="Who can do what" description="Super Users run the organization. Project Managers work only the jobs they are assigned to, as PM Admins (can send texts) or PM Users (everything except sending). The matrix reflects the switches below.">
        <div className="grid gap-3 sm:grid-cols-3">
          {ACCESS_LEVELS.map((l) => (
            <div key={l} className="border p-3">
              <div className="text-sm font-medium">{LEVEL_LABEL[l]}</div>
              <p className="text-muted-foreground mt-1 text-xs leading-snug">{LEVEL_DESCRIPTION[l]}</p>
            </div>
          ))}
        </div>
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Capability</TableHead>
                {ACCESS_LEVELS.map((l) => (
                  <TableHead key={l} className="text-center">
                    {LEVEL_LABEL[l]}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {CAPABILITIES.map((c) => (
                <TableRow key={c.key}>
                  <TableCell>
                    <div className="text-sm">{c.label}</div>
                    {c.hint && <div className="text-muted-foreground text-xs">{c.hint}</div>}
                  </TableCell>
                  {ACCESS_LEVELS.map((l) => {
                    const cell = c[l];
                    const on = cellOn(cell, draft);
                    const configurable = cell !== 'yes' && cell !== 'no';
                    return (
                      <TableCell key={l} className="text-center">
                        <span
                          className={cn('inline-flex size-6 items-center justify-center border', on ? 'border-success/40 text-success' : 'text-muted-foreground/60', configurable && 'border-dashed')}
                          title={configurable ? `Controlled by "${POLICY_LABEL[cell].label}"` : undefined}
                        >
                          {on ? <CheckIcon className="size-3.5" /> : <MinusIcon className="size-3.5" />}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="text-muted-foreground text-xs">Dashed cells follow a switch below. PM Users can never send texts or make someone a PM Admin, whatever the switches say.</p>
      </Section>

      <Section
        title="Project Manager policy"
        description="Narrow what PM Admins and PM Users may do on their jobs. Changes apply the next time each page loads."
        footer={
          <div className="flex gap-2">
            <Button variant="outline" disabled={!dirty || busy} onClick={() => setDraft(policy)}>
              Reset
            </Button>
            <Button disabled={!dirty || busy} onClick={() => void onSave({ access: draft }, 'Access policy saved')}>
              Save access policy
            </Button>
          </div>
        }
      >
        <div className="grid gap-4 lg:grid-cols-2">
          {(['pm_admin', 'pm_user'] as const).map((level) => (
            <div key={level} className="grid gap-3">
              <div className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">{LEVEL_LABEL[level]}s</div>
              {POLICY_KEYS.filter((k) => POLICY_LABEL[k].level === level).map((k) => (
                <label key={k} className="flex items-start justify-between gap-4 border p-3">
                  <span className="grid gap-0.5">
                    <span className="text-sm font-medium">{POLICY_LABEL[k].label}</span>
                    <span className="text-muted-foreground text-xs">{POLICY_LABEL[k].hint}</span>
                  </span>
                  <Switch checked={draft[k]} onCheckedChange={(v) => setDraft((d) => ({ ...d, [k]: v }))} aria-label={POLICY_LABEL[k].label} />
                </label>
              ))}
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function SecurityTab({ view, onSave, busy }: { view: TenantSettingsView; onSave: SaveFn; busy: boolean }) {
  const s = view.settings.security ?? { idleTimeoutMinutes: 0, sessionDays: 30 };
  const [idle, setIdle] = useState(s.idleTimeoutMinutes);
  const [days, setDays] = useState(s.sessionDays);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const summary = useSessionsSummary();
  const revokeAll = useRevokeAllSessions();
  useEffect(() => {
    setIdle(s.idleTimeoutMinutes);
    setDays(s.sessionDays);
  }, [s.idleTimeoutMinutes, s.sessionDays]);
  const dirty = idle !== s.idleTimeoutMinutes || days !== s.sessionDays;

  const signOutEveryone = async () => {
    try {
      const r = await revokeAll.mutateAsync();
      toast.success(r.revoked === 0 ? 'No other devices were signed in' : `Signed out ${r.revoked} ${r.revoked === 1 ? 'device' : 'devices'}`);
      setConfirmSignOut(false);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="grid gap-6">
      <Section
        title="Session policy"
        description="How long people in your organization stay signed in to the web app. Changes apply the next time each browser checks in (within about 15 minutes)."
        footer={
          <Button disabled={!dirty || busy} onClick={() => void onSave({ security: { idleTimeoutMinutes: idle, sessionDays: days } }, 'Session policy saved')}>
            Save session policy
          </Button>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Sign out when inactive" htmlFor="sec-idle" description="Activity in any tab counts. The sign-in page explains why they were signed out.">
            <select id="sec-idle" className={selectClass} value={idle} onChange={(e) => setIdle(Number(e.target.value))}>
              {IDLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Stay signed in for" htmlFor="sec-days" description="Longest a device stays signed in without signing in again. The platform may cap this.">
            <select id="sec-days" className={selectClass} value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {SESSION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Section>

      <Section title="Signed-in devices" description="Every browser that is currently signed in to your organization.">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LockIcon className="text-muted-foreground size-4" />
            <div>
              <div className="text-sm font-medium">{summary.isPending ? 'Counting…' : `${summary.data?.activeSessions ?? 0} active ${summary.data?.activeSessions === 1 ? 'session' : 'sessions'}`}</div>
              <p className="text-muted-foreground text-xs">Signing everyone out forces a fresh sign-in on every other device. Yours stays signed in.</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => setConfirmSignOut(true)} disabled={revokeAll.isPending}>
            Sign everyone out
          </Button>
        </div>
      </Section>

      <ConfirmDialog
        open={confirmSignOut}
        onOpenChange={setConfirmSignOut}
        title="Sign everyone out?"
        description="Every other device in your organization is signed out immediately and must sign in again. Your own session stays active."
        confirmLabel="Sign everyone out"
        destructive
        loading={revokeAll.isPending}
        onConfirm={signOutEveryone}
      />
    </div>
  );
}
